import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import TextButton from "@shared/v2/TextButton";
import Tooltip from "@shared/v2/Tooltip";
import Typography from "@shared/v2/Typography";
import { InfoCircleSolidV3, CaretDownSolidV6, PencilSolidV6 } from "@shared/v2/Icomoon";

const DisplayRow = ({
  title,
  tooltip = null,
  tooltipIconClassName = "",
  editable,
  alwaysEdit = false,
  value = null,
  onChange,
  onSave,
  Comp = null,
  errorHandler = null,
  displayValue = null,
  isDD = false,
}) => {
  const firstUpdate = useRef(true);
  const [isEditing, setEditing] = useState(false);

  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    if (isEditing) {
      return;
    }
    onSave(value);
  }, [value, isEditing]);

  const renderValue = value && !alwaysEdit && !isEditing;
  const renderNewBtn = (!value || value <= "") && editable && !alwaysEdit && !isEditing;
  const renderEmptyValue = (!value || value <= "") && !editable && !alwaysEdit;
  const renderEditComp = alwaysEdit || isEditing;
  const Component = editable ? "button" : "div";

  return (
    <div className="tw-relative tw-flex tw-justify-between tw-gap-[16px] tw-items-center tw-min-h-[36px]">
      <div className="tw-flex tw-items-center tw-gap-[4px]">
        <Typography variant="subtitle-m" className="tw-whitespace-nowrap !tw-text-neutral-gray-50">
          {title}
        </Typography>
        {tooltip && (
          <Tooltip
            multiline
            placement="right"
            innerClassName="tw-max-w-[264px]"
            content={tooltip}
            trigger={<InfoCircleSolidV3 className={`tw-text-black ${tooltipIconClassName}`} />}
          />
        )}
      </div>
      <div
        className={`group tw-flex tw-overflow-hidden tw-whitespace-nowrap ${renderValue && editable ? "tw-mr-[-16px]" : ""}`}
      >
        {renderValue && (
          <Component
            type="button"
            onClick={editable ? () => setEditing(true) : null}
            className={`tw-overflow-hidden tw-flex tw-items-center tw-group tw-p-0 tw-bg-transparent tw-border-0 active:tw-text-[var(--text-button-color)] ${
              !editable ? "tw-cursor-default" : ""
            }`}
          >
            <span className="tw-flex-1 tw-basis-[content] tw-overflow-hidden tw-text-ellipsis">
              {displayValue ? displayValue(value) : value}
            </span>
            {editable && (
              <span className="group-hover:tw-visible tw-invisible tw-ml-[4px]">
                {isDD ? <CaretDownSolidV6 size="m" /> : <PencilSolidV6 size="m" />}
              </span>
            )}
          </Component>
        )}
        {renderNewBtn && (
          <TextButton onClick={() => setEditing(true)} className="tw-px-0">
            Add
          </TextButton>
        )}
        {renderEmptyValue && "—"}
        {renderEditComp && (
          <Comp
            value={value}
            onChange={(val) => onChange(val)}
            onBlur={() => {
              const error = errorHandler ? errorHandler(value) : null;
              if (error) {
                onSave(value);
                onChange(value);
              } else {
                setEditing(false);
              }
            }}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                e.target?.blur();
                const error = errorHandler ? errorHandler(value) : null;
                if (error) {
                  onSave(value);
                  onChange(value);
                } else {
                  setEditing(false);
                }
              }
            }}
          />
        )}
      </div>
    </div>
  );
};

DisplayRow.propTypes = {
  title: PropTypes.string.isRequired,
  tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  tooltipIconClassName: PropTypes.string,
  editable: PropTypes.bool.isRequired,
  alwaysEdit: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  onChange: PropTypes.func.isRequired,
  Comp: PropTypes.func,
  onSave: PropTypes.func.isRequired,
  displayValue: PropTypes.func,
  errorHandler: PropTypes.func,
  isDD: PropTypes.bool,
};

export default DisplayRow;
